"use client"

import React, { createContext, useContext, useReducer, useEffect } from 'react'
import Cookies from 'js-cookie'

export interface CartItemAddOn {
  name: string
  price: number
}

export interface CartItem {
  id: number
  name: string
  price: number
  quantity: number
  image?: string
  // Variants/add-ons captured from the detail page. Optional so the legacy
  // "Add to cart" path on the catalog grid keeps working without options.
  selectedOptions?: Record<string, string>
  selectedAddOns?: CartItemAddOn[]
  // Stable identifier for this configured line. Two carts of the same item id
  // with different selections become separate lines.
  lineKey: string
}

interface CartState {
  items: CartItem[]
  total: number
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: Omit<CartItem, 'quantity' | 'lineKey'> & { lineKey?: string } }
  | { type: 'REMOVE_ITEM'; payload: { lineKey: string } }
  | { type: 'INCREMENT_QUANTITY'; payload: { lineKey: string } }
  | { type: 'DECREMENT_QUANTITY'; payload: { lineKey: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { lineKey: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; payload: CartItem[] }

function computeLineKey(payload: { id: number; selectedOptions?: Record<string, string>; selectedAddOns?: CartItemAddOn[] }): string {
  const optionsKey = payload.selectedOptions
    ? Object.keys(payload.selectedOptions).sort().map(k => `${k}=${payload.selectedOptions![k]}`).join('&')
    : ''
  const addOnsKey = payload.selectedAddOns?.length
    ? [...payload.selectedAddOns].map(a => a.name).sort().join('+')
    : ''
  if (!optionsKey && !addOnsKey) return String(payload.id)
  return `${payload.id}|${optionsKey}|${addOnsKey}`
}

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const lineKey = action.payload.lineKey ?? computeLineKey(action.payload)
      const existingItem = state.items.find(item => item.lineKey === lineKey)

      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.lineKey === lineKey
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
          total: state.total + action.payload.price
        }
      } else {
        const newItem: CartItem = { ...action.payload, lineKey, quantity: 1 }
        return {
          ...state,
          items: [...state.items, newItem],
          total: state.total + action.payload.price
        }
      }
    }

    case 'REMOVE_ITEM': {
      const itemToRemove = state.items.find(item => item.lineKey === action.payload.lineKey)
      if (!itemToRemove) return state

      return {
        ...state,
        items: state.items.filter(item => item.lineKey !== action.payload.lineKey),
        total: state.total - (itemToRemove.price * itemToRemove.quantity)
      }
    }

    case 'INCREMENT_QUANTITY': {
      const item = state.items.find(item => item.lineKey === action.payload.lineKey)
      if (!item) return state

      return {
        ...state,
        items: state.items.map(item =>
          item.lineKey === action.payload.lineKey
            ? { ...item, quantity: item.quantity + 1 }
            : item
        ),
        total: state.total + item.price
      }
    }

    case 'DECREMENT_QUANTITY': {
      const item = state.items.find(item => item.lineKey === action.payload.lineKey)
      if (!item || item.quantity <= 1) return state

      return {
        ...state,
        items: state.items.map(item =>
          item.lineKey === action.payload.lineKey
            ? { ...item, quantity: item.quantity - 1 }
            : item
        ),
        total: state.total - item.price
      }
    }

    case 'UPDATE_QUANTITY': {
      const item = state.items.find(item => item.lineKey === action.payload.lineKey)
      if (!item) return state

      const quantityDiff = action.payload.quantity - item.quantity
      const priceDiff = quantityDiff * item.price

      return {
        ...state,
        items: state.items.map(item =>
          item.lineKey === action.payload.lineKey
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
        total: state.total + priceDiff
      }
    }

    case 'CLEAR_CART':
      return {
        items: [],
        total: 0
      }

    case 'LOAD_CART': {
      const normalized = action.payload.map(item => ({
        ...item,
        lineKey: item.lineKey ?? computeLineKey(item),
      }))
      const total = normalized.reduce((sum, item) => sum + (item.price * item.quantity), 0)
      return {
        items: normalized,
        total
      }
    }

    default:
      return state
  }
}

interface CartContextType {
  state: CartState
  addItem: (item: Omit<CartItem, 'quantity' | 'lineKey'> & { lineKey?: string }) => void
  removeItem: (lineKey: string) => void
  incrementQuantity: (lineKey: string) => void
  decrementQuantity: (lineKey: string) => void
  updateQuantity: (lineKey: string, quantity: number) => void
  clearCart: () => void
  getItemQuantity: (lineKey: string) => number
  getCartItemCount: () => number
  getTotalPrice: () => number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const useCart = () => {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

interface CartProviderProps {
  children: React.ReactNode
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    total: 0
  })

  // Load cart from cookie on mount
  useEffect(() => {
    const savedCart = Cookies.get('cart')
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart)
        if (Array.isArray(parsedCart)) {
          dispatch({ type: 'LOAD_CART', payload: parsedCart })
        } else {
          dispatch({ type: 'LOAD_CART', payload: parsedCart.items || [] })
        }
      } catch (error) {
        console.error('Error loading cart from cookie:', error)
      }
    }
  }, [])

  // Save cart to cookie whenever it changes
  useEffect(() => {
    Cookies.set('cart', JSON.stringify({
      items: state.items,
      total: state.total
    }), { expires: 7 })
  }, [state.items, state.total])

  const addItem: CartContextType['addItem'] = (item) => {
    dispatch({ type: 'ADD_ITEM', payload: item })
  }

  const removeItem = (lineKey: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { lineKey } })
  }

  const incrementQuantity = (lineKey: string) => {
    dispatch({ type: 'INCREMENT_QUANTITY', payload: { lineKey } })
  }

  const decrementQuantity = (lineKey: string) => {
    dispatch({ type: 'DECREMENT_QUANTITY', payload: { lineKey } })
  }

  const updateQuantity = (lineKey: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(lineKey)
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { lineKey, quantity } })
    }
  }

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' })
  }

  const getItemQuantity = (lineKey: string) => {
    const item = state.items.find(item => item.lineKey === lineKey)
    return item ? item.quantity : 0
  }

  const getCartItemCount = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0)
  }

  const getTotalPrice = () => {
    return state.total
  }

  const value: CartContextType = {
    state,
    addItem,
    removeItem,
    incrementQuantity,
    decrementQuantity,
    updateQuantity,
    clearCart,
    getItemQuantity,
    getCartItemCount,
    getTotalPrice
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}
